import React, {useEffect, useState} from "react";
import {ColoredColumns} from "../../Utilities";
import {Badge, Button, Card, Col, message, Popconfirm, Row, Space, Table, Typography} from "antd";
// @ts-ignore
import {JsonEditor} from "jsoneditor-react";
import moment from "moment";
import {AsyncDataSource, ManualPagedTable} from "../Misc/Table/ManualPagedTable";
import JobsService, {InsJob, InsJobLog} from "../../services/JobsService";
import {PresetStatusColorType} from "antd/lib/_util/colors";
import { IAsyncDataSource } from "../Misc/Table/AsyncDataSourceGeneric";
import {useFetch} from "../../hooks/useFetch";

const { Title } = Typography;


class DataSource implements IAsyncDataSource<InsJob> {
    private readonly _curProjectId: string | null;

    constructor(curProjectId: string | null = null) {
        this._curProjectId = curProjectId;
    }

    async values(page: number, limit: number): Promise<InsJob[]> {
        const data = await JobsService.inspectJobs(page, limit, this._curProjectId).catch(err => {
            message.error("Failed to load jobs");
            return [];
        });
        return data;
    }
}


export function ProjectJobs(props: any) {
    const curProjectId = props.match.params.projectId;
    return (
        <Jobs curProjectId={curProjectId}/>
    );
}

type SetDetailsSource = (detailsSource: DetailsSource) => void;
type DetailsSource = {
    details: () => JSX.Element,
    colorOf: (record: any) => string,
    actions: () => any[],
};

export function Jobs({curProjectId=null}: {curProjectId?: string | null}) {
    const [dataSource, setDataSource] = useState(new DataSource(curProjectId));
    const [detailsSource, setDetailsSource] = useState<DetailsSource>({
        colorOf: (record: any) => "",
        details: () => (<></>),
        actions: () => []
    });

    const rowColor = (text: string, record: any, idx: number) => detailsSource.colorOf(record);

    return (
        <>
            <Row style={{padding: "1em"}}>
                <Title level={4}>Jobs</Title>
            </Row>
            <Row gutter={16}>
                <Col span={14}>
                    <Card title={"List"} size={"small"}>
                        <JobsTable
                            asyncDataSource={dataSource}
                            setDetailsSource={setDetailsSource}
                            rowColor={rowColor}
                            size={"small"}
                        />
                    </Card>
                </Col>
                <Col span={10}>
                    <Card title={"Details"} size={"small"} actions={[detailsSource.actions()]}>
                        {detailsSource.details()}
                    </Card>
                </Col>
            </Row>
        </>
    );
}

function JobDetails({job}: {job: InsJob}) {
    const [celeryStatus, setCeleryStatus] = useState<string | null>(null);

    useFetch(
        () => JobsService.jobCeleryStatus(job.id),
        setCeleryStatus,
        () => message.error("Failed to fetch job celery status"),
        [job.id]
    );

    return (
        <>
            <JsonEditor key={job.id} value={{args: job.args}} mode="view"/>
            <Space direction="vertical" style={{marginTop: "16px"}}>
                <RelatedJobInfo job={job} entityIndex="workspace" entityAlias="Workspace"/>
                <RelatedJobInfo job={job} entityIndex="project" entityAlias="Project"/>
                <RelatedJobInfo job={job} entityIndex="user" entityAlias="User"/>
                <span>Celery status: {celeryStatus ?? "UNDEFINED"}</span>
            </Space>
        </>
    );
}

function RelatedJobInfo({job, entityIndex, entityAlias}: {
    job: InsJob,
    entityIndex: "workspace" | "project" | "user",
    entityAlias: string,
}) {
    const entity = job[entityIndex];

    if (!entity) return null;

    return (
        <div id={`related-info_${entityIndex}`}>
            {entityAlias}: {entity.name}
        </div>
    );
}

function JobLogDetails({log}: {log: InsJobLog}) {
    return (
        <JsonEditor key={log.id} value={{payload: log.payload}} mode="view"/>
    );
}

function Status({name, payload}: {name: string, payload: any}) {
    let status: PresetStatusColorType = "default";

    if (name === "FINISHED") {
        status = "success";
    } else if (name === "FAILED") {
        status = "error";
    } else if (["CUSTOM_PROGRESS", "PROGRESS", "RUNNING"].includes(name)) {
        status = "processing";
    } else if (["SCHEDULED", "RETRY_SCHEDULED"].includes(name)) {
        status = "warning";
    }

    return (
        <span>
            <Badge status={status}/>
            {payload.data?.status ?? name}
        </span>
    );
}

type JobsTableRecord = {
    id: string,
    key: string,
    type: string,
    status: JSX.Element,
    duration: string,
    created_at: string,
    logs: InsJobLog[],
    onClick: (e: any) => void,
}

function JobsTable({asyncDataSource, rowColor, setDetailsSource, ...props}: {
    asyncDataSource: IAsyncDataSource<InsJob>,
    rowColor: RowColor,
    setDetailsSource: SetDetailsSource,
    size: string,
}) {
    const [jobsSource, setJobsSource] = useState<IAsyncDataSource<JobsTableRecord>>(
        new AsyncDataSource()
    );

    useEffect(() => {
        const detailsSourceOf = (job: InsJob) => ({
            colorOf: (record: JobsTableRecord) => {
                return record.key === job.id ? "#eee" : "";
            },
            details: () => {
                return <JobDetails job={job}/>;
            },
            actions: () => [
                <Popconfirm
                    title={"Are you sure you want to terminate this job?"}
                    key={"terminate"}
                    onConfirm={async () => {
                        try {
                            const result = await JobsService.terminateJob(job.id);
                            if (result) {
                                message.success("Terminated");
                            } else {
                                message.warning("Nothing to Terminate");
                            }
                        } catch (_) {
                            message.error("Failed to terminate job");
                        }
                    }}
                >
                    <Button type={"primary"}>Terminate</Button>
                </Popconfirm>
            ]
        });

        const createdAtOf = (job: InsJob) => {
            const firstLog = job.logs[0];

            if (firstLog == null) {
                return "UNKNOWN";
            }

            return moment.utc(firstLog.created_at).local().format("MM/DD/YYYY, h:mm:ss:SSS a");
        };

        const durationOf = (job: InsJob) => {
            const firstLog = job.logs[0];
            const lastLog = job.logs.slice(-1)[0];

            if (firstLog === lastLog) {
                return "UNKNOWN";
            }

            return `${new Date(lastLog.created_at).getTime() - new Date(firstLog.created_at).getTime()}ms`;
        };

        const statusOf = (job: InsJob) => {
            const lastLog = job.logs.slice(-1)[0];
            return <Status name={lastLog?.status || "UNKNOWN"} payload={lastLog?.payload}/>;
        };

        setJobsSource(
            {
                values: async (page, limit) => {
                    const jobs = await asyncDataSource.values(page, limit);

                    const records = jobs.map(job => ({
                        id: job.id,
                        key: job.id,
                        type: job.type,
                        status: statusOf(job),
                        duration: durationOf(job),
                        created_at: createdAtOf(job),
                        logs: job.logs,
                        onClick: () => setDetailsSource(detailsSourceOf(job))
                    }));

                    return records;
                }
            }
        );
    }, [asyncDataSource, setDetailsSource]);

    const expandedRowRender = (record: JobsTableRecord) => {
        return (
            <JobLogsTable
                key={record.key}
                logs={record.logs}
                rowColor={rowColor}
                setDetailsSource={setDetailsSource}
            />
        );
    };

    const cellProps = (record: JobsTableRecord) => ({
        onClick: (e: any) => record.onClick(e)
    });

    const coloredColumns = new ColoredColumns(
        [
            {
                title: "Id",
                dataIndex: "id",
                onCell: cellProps
            },
            {
                title: "Type",
                dataIndex: "type",
                onCell: cellProps
            },
            {
                title: "Status",
                dataIndex: "status",
                onCell: cellProps
            },
            {
                title: "Created at",
                dataIndex: "created_at",
                onCell: cellProps
            },
            {
                title: "Duration",
                dataIndex: "duration",
                onCell: cellProps
            }
        ],
        rowColor
    );

    return (
        <ManualPagedTable
            {...props}
            id="jobs-table"
            columns={coloredColumns.values()}
            asyncDataSource={jobsSource}
            scroll={{x: 400}}
            size="small"
            expandable={{ expandedRowRender }}
        />
    );
}

type JobLogsTableRecord = {
    key: string,
    status: JSX.Element,
    created_at: string,
    onClick: (e: any) => void
}

function JobLogsTable({logs, rowColor, setDetailsSource}: {
    logs: InsJobLog[],
    rowColor: RowColor,
    setDetailsSource: SetDetailsSource,
}) {
    const [records, setRecords] = useState<JobLogsTableRecord[]>([]);

    useEffect(() => {
        const detailsSourceOf = (log: InsJobLog) => ({
            colorOf: (record: JobLogsTableRecord) => {
                return record.key === log.id ? "#eee" : "";
            },
            details: () => {
                return <JobLogDetails log={log}/>;
            },
            actions: () => []
        });

        const statusOf = (log: InsJobLog) => {
            return <Status name={log.status} payload={log.payload}/>;
        };

        setRecords(
            logs.map((log: InsJobLog) => ({
                key: log.id,
                status: statusOf(log),
                created_at: moment.utc(log.created_at).local().format("MM/DD/YYYY, h:mm:ss:SSS a"),
                onClick: () => setDetailsSource(detailsSourceOf(log))
            }))
        );
    }, [logs]);

    const cellProps = (record: JobLogsTableRecord) => ({
        onClick: (e: any) => record.onClick(e)
    });

    const coloredColumns = new ColoredColumns(
        [
            {
                title: "Status",
                dataIndex: "status",
                onCell: cellProps
            },
            {
                title: "Date time",
                dataIndex: "created_at",
                onCell: cellProps
            },
        ],
        rowColor
    );

    return (
        <Table
            id="job-logs-table"
            scroll={{x: 400}}
            size="small"
            pagination={(records.length > 10) as any}
            dataSource={records}
            columns={coloredColumns.values()}
        />
    );
}

type RowColor = any;